export interface FunctionHelp {
  title: string;
  text: string[];
  available: boolean;
}

/**
 * Kanonische Hilfe für stabile Dashboard-Funktionstitel.
 *
 * WICHTIG:
 * - Keine generische Fallback-Hilfe für unbekannte/dynamische Titel.
 * - Exakte Titel bleiben erhalten; Klammer-Zusatztexte werden NICHT entfernt.
 * - Frei benannte Karten (Feedname, Embedname, Spielername usw.) bekommen damit
 *   nicht versehentlich einen inhaltlich falschen Hilfe-Button.
 */
const HELP: Record<string, string[]> = {
  'Virtuelle Konten': [
    'Hier verwaltest du getrennte CUSTOM-, Gruppen-, Projekt- und Systemkonten dieses Gameservers. Wallet, Bankanteil, Kontoverwalter und Discord-Projektion bleiben pro Konto getrennt.',
    'Buchungen bleiben im Economy-Ledger nachvollziehbar. Systemkonten für Lotterie und Schwarzmarkt werden über ihre jeweiligen Fachbereiche gesteuert.',
  ],
  'Server-Toggles': [
    'Hier schaltest du Whitelist, Economy und Perma-Only für genau diesen Gameserver ein oder aus. Jeder Schalter wird separat gespeichert.',
    'Perma-Only verhindert keinen geplanten Stopp. Wird ein unerwartet offline erkannter Gameserver festgestellt, veranlasst V-Bot über den bestehenden Nitrado-Jobpfad einen Wiederstart.',
  ],
  Bank: [
    'Hier konfigurierst du die servergescoppte Bankfunktion, den Bank-Channel und die zugehörigen Economy-Einstellungen dieses Slots.',
    'Kontostände und Buchungen bleiben im bestehenden Ledger; die Anzeige selbst führt keine automatische Umbuchung aus.',
  ],
  Serverbank: [
    'Die Serverbank ist das zentrale CUSTOM-Konto dieses Slots. Wallet und Bankreserve werden getrennt geführt.',
    'Guthaben wird bei keiner normalen Verwaltungsaktion stillschweigend verworfen; Bewegungen bleiben nachvollziehbar.',
  ],
  'Management-Kanal': [
    'Hier liegt das Discord-Panel für Kontoverwalter. Nur zugewiesene Personen können ihre freigegebenen Konten bedienen.',
    'Die Freigabe gilt innerhalb des ausgewählten Gameserver-Slots und ersetzt keine globale Dashboard-Berechtigung.',
  ],
  'Admin-Auszahlung': [
    'Zahlt Guthaben aus einem CUSTOM-Konto an ein Discord-Mitglied aus.',
    'Betrag, Ziel, verfügbares Guthaben und Buchung werden vor der Auszahlung geprüft und im Economy-Ledger protokolliert.',
  ],
  Lotterie: [
    'Hier erstellst und verwaltest du Lotterie-Runden für diesen Gameserver. Ticketkäufe werden dem Lotterie-Pot zugeordnet; Ziehung, Auszahlung und Rückerstattung werden als Economy-Vorgänge protokolliert.',
    'Auszahlung und Refund sind idempotent abgesichert. Wird eine Runde nicht regulär abgeschlossen, bleibt ihr Status nachvollziehbar und kann kontrolliert zurückerstattet werden.',
  ],
  Schwarzmarkt: [
    'Hier verwaltest du Händler, Angebote und Bestellungen dieses Gameservers. Käufe bleiben als PENDING, DELIVERED, REFUNDED oder LEGACY nachvollziehbar.',
    'Der Schwarzmarkt spawnt gekaufte DayZ-Gegenstände nicht automatisch auf dem Gameserver. Die Erfüllung wird bewusst bestätigt oder bei Bedarf zurückgezahlt.',
  ],
  'Economy-Status': [
    'Zeigt den aktuellen Economy-Zustand dieses Gameservers. Die Daten gelten nur für den ausgewählten Slot.',
    'Die Ansicht liest vorhandene Economy-Daten und nimmt durch das Anzeigen oder Aktualisieren keine Buchung vor.',
  ],
  'Economy-Konfiguration': [
    'Hier legst du Währungsname, Emoji, Startguthaben und servergescoppte Economy-Regeln fest.',
    'Startguthaben und Reward-Werte wirken auf zukünftige Initialisierungen bzw. Buchungen; Währungsname und Emoji ändern die Darstellung. Bestehende Ledger-Buchungen und Guthaben werden nicht rückwirkend umgeschrieben.',
  ],
  'Economy-Links (Discord ↔ In-Game)': [
    'Verbindet ein Discord-Mitglied mit dem verifizierten DayZ-Spieler dieses Gameservers. Dadurch können Economy-, Session- und Gameplay-Vorgänge dem richtigen Konto zugeordnet werden.',
    'Die Zuordnung ist servergescoppt. Eine bloße Namensähnlichkeit wird nicht als verifizierter Link behandelt.',
  ],
  Whitelist: [
    'Hier verwaltest du den Zugang zum ausgewählten Gameserver. Einträge, Anträge und Synchronisierung gelten nur für diesen Slot.',
    'Vorschau und Apply sind getrennt: Eine Sync-Vorschau liest und vergleicht lokale sowie aktuelle Nitrado-Daten, während erst Apply die gewählte Synchronisierung ausführt.',
  ],
  'Whitelist (Spielername)': [
    'Hier verwaltest du die servergescoppte Whitelist nach DayZ-Spielername. Hinzufügen und Entfernen werden über den bestehenden Nitrado-Write-Pfad verarbeitet.',
    'Aktive Server-Bans werden vor widersprüchlichen Whitelist-Freigaben geprüft; Remote-Änderungen werden nicht für andere Slots übernommen.',
  ],
  'Pending-Requests': [
    'Zeigt offene Whitelist-Anträge dieses Gameservers. Entscheidungen dürfen der Server-Owner oder Mitglieder mit der V-Bot-Berechtigung whitelist.manage treffen.',
    'Eine Annahme legt den lokalen Whitelist-Eintrag an und reiht die Nitrado-Synchronisierung ein. Eine aktive servergescoppte Ban-Sperre blockiert die Freigabe fail-closed.',
  ],
  'Kanal-Integration (Whitelist)': [
    'Hier legst du fest, in welchen Discord-Kanälen Whitelist-Informationen, Anträge, Freigaben, Ablehnungen und Kataloge für diesen Gameserver erscheinen.',
    'Whitelist-Entscheidungen richten sich nach den V-Bot-Berechtigungen, insbesondere whitelist.manage; Discord „Server verwalten“ ist dafür kein eigener Berechtigungsersatz.',
  ],
  'Nitrado Gameplay-Feeds': [
    'Hier richtest du Discord-Meldungen aus kanonischen DayZ-ADM-Ereignissen für diesen Gameserver ein. Die Pipeline arbeitet servergescoppt, persistent und mit Event-Deduplizierung sowie Retry.',
    'Der Killfeed ist PvP-bezogen; weitere ADM-Kategorien werden getrennt behandelt. Das Konfigurieren der Ausgabe verändert keine Nitrado-Gameserverdateien.',
  ],
  'Willkommensnachricht': [
    'Hier richtest du Kanal, Text, optionale Medien und Platzhalter für neue Discord-Mitglieder ein.',
    'Die Vorschau ersetzt Platzhalter lokal mit Beispielwerten. Eine echte Testnachricht wird nur versendet, wenn du sie ausdrücklich auslöst.',
  ],
  'Onboarding-Module': [
    'Zeigt die Funktionen für neue Mitglieder dieses Discord-Servers. Willkommen, Testnachricht und Auto-Rollen werden hier serverweit verwaltet.',
    'Diese Guild-Funktionen sind nicht an einen Nitrado-Slot gebunden.',
  ],
  'Vorschau & Test': [
    'Die Vorschau ersetzt Platzhalter lokal mit Beispielwerten und zeigt die konfigurierte Reihenfolge von Bild und Text.',
    'Erst „Testnachricht senden“ löst eine reale Discord-Nachricht aus.',
  ],
  'Auto-Rollen': [
    'Hier verwaltest du Rollen, die neuen Mitgliedern anhand der gespeicherten Trigger automatisch zugewiesen werden.',
    'Discord-Integrationsrollen werden nicht als normale zuweisbare Rollen angeboten. Der vorhandene /autorole-Command nutzt dieselbe Datenhaltung.',
  ],
  Fraktionssystem: [
    'Hier erstellst und verwaltest du Discord-Fraktionen, Rollen, Mitglieder und optionale Fraktions-Embeds.',
    'Fraktionen sind an den Discord-Server gebunden und bewusst unabhängig von einem Nitrado-Slot.',
  ],
  Feeds: [
    'Hier verwaltest du RSS-, News-, Twitch-, Steam-, YouTube- und eingehende Webhook-Feeds für den Discord-Server. Zielkanal, Intervall und optionale Rollen-Pings werden pro Feed gespeichert.',
    'Per-Feed Zugangsdaten werden nur beim Speichern geschrieben und nicht wieder als Klartext geladen. Legacy-Feedtypen bleiben sichtbar, können aber nicht neu erstellt oder reaktiviert werden.',
  ],
  'Eingebettete Nachrichten': [
    'Hier erstellst und verwaltest du wiederverwendbare Discord-Embeds mit Feldern, Medien, Farben und Zielkanal.',
    'Die Vorschau aktualisiert sich lokal während der Bearbeitung. Auf Discord wird erst durch Senden oder Synchronisieren eingewirkt; Speichern allein kann einen Entwurf erhalten.',
  ],
  'Reaktions-Embeds': [
    'Hier erstellst du Rollenmenüs als Buttons, Dropdown oder Emoji-Reaktion. Optionen können GIVE, REMOVE oder TOGGLE verwenden und als SINGLE- oder MULTI-Auswahl arbeiten.',
    'Rollen, Optionsgrenzen und verknüpfte Embeds werden vor dem Speichern bzw. Veröffentlichen validiert.',
  ],
  Übersetzungen: [
    'Hier erstellst du automatisch übersetzte Discord-Posts für sofortigen, einmaligen oder wiederkehrenden Versand.',
    'Du bestimmst Quell- und Zielsprache, Zielkanal, optionale Bilder, Rollen-Pings und den Versandmodus.',
  ],
  'Spielerdaten löschen': [
    'Wenn der Schalter aktiv ist, entfernt V-Bot bei zukünftigen Guild-Austritten den verifiziert zugeordneten DayZ-Spieler von der Nitrado-Whitelist und setzt nur die guild-/gameservergescoppte DayZ-Gameplay-Statistik zurück.',
    'Linking, Economy, Wallet/Bank/Rewards, XP, Level, Identitäts- und Moderationsdaten bleiben erhalten. Bereits persistent eingereihte Cleanup-Aufträge werden auch nach dem Ausschalten konsistent zu Ende verarbeitet.',
  ],
  'Bye Bye': [
    'Hier aktivierst du das vordefinierte V-Bot-Abschieds-Embed für Mitglieder, die den Discord-Server verlassen, und wählst den Zielkanal.',
    'Beim Aktivieren werden Guild-Zuordnung und benötigte Discord-Berechtigungen des Zielkanals geprüft. Der Abschiedstext ist nicht frei editierbar und löst keine Rollen-Pings aus.',
  ],
  'Moderations-Log': [
    'Diese Ansicht zeigt Discord-Moderationsfälle wie Kick, Bann, Stummschaltung und Verwarnung sowie offene Appeals dieses Servers.',
    'Sie ist rein lesend. DayZ-/Nitrado-Bans sind bewusst nicht enthalten und werden im eigenen servergescoppten Bereich verwaltet.',
  ],
  'Offene Anträge': [
    'Zeigt offene Appeals zu Discord-Moderationsfällen. Die Entscheidung selbst bleibt bei den vorhandenen Moderationsbefehlen.',
    'Diese Ansicht verändert keinen Fall und enthält keine DayZ-/Nitrado-Bans.',
  ],
  'XP & Level-Verwaltung': [
    'Zeigt das bestehende guildweite XP-/Level-System mit Level, XP, Nachrichten- und Sprachzeitzählern.',
    'Ein Einzel-Reset setzt den ausgewählten Nutzer zurück. Die XP-Schwellen und Multiplikatoren werden hier bewusst nicht konfiguriert.',
  ],
  'Massen-Reset': [
    'Setzt Level, XP, Nachrichten- und Sprachzeitzähler für alle Mitglieder dieses Discord-Servers zurück.',
    'Die Aktion ist nicht rückgängig zu machen und erfordert deshalb die ausdrückliche Bestätigung RESET_ALL.',
  ],
  'Radar-Karte': [
    'Zeigt die aktive DayZ-Karte dieses Slots mit gespeicherten Radar-Zonen und kurzlebigen Live-Markern aus kanonischen ADM-Ereignissen.',
    'Live-Marker sind eine temporäre Echtzeitanzeige und keine vollständige historische Spielerüberwachung. Die Kartenwahl selbst verändert keine DayZ-Mapdatei.',
  ],
  'Gespeicherte Zonen': [
    'Hier verwaltest du Kreis- oder Polygon-Zonen für die aktive DayZ-Karte. Funktionen, Rollen-Pings und Allowlist-Ausnahmen werden pro Zone gespeichert.',
    'Erkennung und Bestrafung sind getrennt: Nur ausdrücklich punitive Funktionen können eine Ban-Regel auslösen.',
  ],
  'Radar-Funktionen': [
    'Zeigt die verfügbaren Radar-Regeln dieses Systems. Nicht punitive Regeln erkennen oder melden Ereignisse; punitive Regeln sind ausdrücklich als Auto-Ban-Regeln gekennzeichnet.',
    'Allowlist-Ausnahmen und Rollen-Pings werden getrennt von der eigentlichen Erkennungs-/Ban-Funktion konfiguriert.',
  ],
  'Connection & API': [
    'Zeigt den persistierten Zustand der Nitrado-Verbindung, Service-Bindung und letzten Validierung dieses Slots.',
    'Das Anzeigen oder Aktualisieren dieser Karte startet keine Nitrado-Operation.',
  ],
  'ADM Pipeline': [
    'Zeigt, ob eine ADM-Quelle konfiguriert ist, wann zuletzt erfolgreich eingelesen wurde und welche offenen ADM-Sessions bekannt sind.',
    'Die Werte stammen aus der bestehenden ADM-Pipeline; die Karte startet keinen neuen Server-Read oder Write.',
  ],
  'Nitrado Job Worker': [
    'Zeigt den Zustand der servergescoppten Nitrado-Jobqueue sowie READ- und WRITE-Circuit-Breaker.',
    'PENDING, RUNNING, FAILED und DEAD beschreiben persistierte Jobzustände. Die Ansicht führt selbst keinen Job aus.',
  ],
  Mirror: [
    'Zeigt den letzten gespeicherten Mirror-Snapshot und bekannte Snapshot-Fehler dieses Gameservers.',
    'Der Mirror ist Grundlage für die read-only Zeitmaschine. Diese Ansicht schreibt keine Datei auf den Gameserver zurück.',
  ],
  'Bekannte Probleme': [
    'Zeigt Problemmarker, die ausschließlich aus bereits vorhandenen Status- und Fehlerdaten abgeleitet werden.',
    'Die Marker sind Diagnosehinweise; das Dashboard startet dadurch keine automatische Reparatur oder Nitrado-Aktion.',
  ],
  'Snapshot-Historie': [
    'Zeigt gespeicherte read-only Mirror-Snapshots dieses Gameservers mit Status, Dateizahl, Größe und Fehleranzahl.',
    'Snapshots sind Vergleichsgrundlage und kein Restore- oder Deploy-Mechanismus.',
  ],
  'Snapshots vergleichen': [
    'Vergleicht zwei gespeicherte Mirror-Snapshots und ordnet Dateien als neu, entfernt, geändert, unverändert oder unbekannt ein.',
    '„Unverändert“ wird nur behauptet, wenn die vorhandenen SHA-256-Werte beider Stände identisch sind.',
  ],
  Dateien: [
    'Listet die Dateiergebnisse des gewählten Snapshot-Vergleichs. Der Filter verändert nur die Darstellung.',
    'Ein Text-Diff ist nur für geeignete, gespeicherte Textdateien innerhalb der Größenlimits verfügbar.',
  ],
  'Text-Diff': [
    'Zeigt einen read-only Zeilenvergleich einer geeigneten Textdatei zwischen zwei Mirror-Snapshots.',
    'Der Diff kann nichts auf Nitrado wiederherstellen, deployen oder überschreiben.',
  ],
  Appeals: [
    'Hier prüfst du Einsprüche gegen Moderationsentscheidungen und kannst den Status genehmigen, ablehnen oder eskalieren.',
    'Die Entscheidung wird protokolliert; vor dem Bestätigen sollten Fall und Begründung geprüft werden.',
  ],
  Feedback: [
    'Hier sichtest und bearbeitest du Nutzer-Feedback mit Status und optionaler Admin-Notiz.',
    'Das Bearbeiten eines Feedback-Eintrags führt keine technische Änderung an der gemeldeten Funktion aus.',
  ],
  Broadcast: [
    'Versendet direkte Discord-Nachrichten an die gewählte Nutzergruppe. Der Probelauf zählt die Empfänger, ohne die Nachricht zu senden.',
    '„ALLE“ betrifft die komplette unterstützte Zielgruppe und sollte nur bewusst verwendet werden; der echte Versand erfordert eine separate Bestätigung.',
  ],
  'Upload-Steuerung': [
    'Schaltet Uploads im Bot-Admin-Bereich global ein oder aus und zeigt erlaubte Dateitypen sowie die aktuelle Größenbegrenzung.',
    'Das Umschalten verändert keine bereits gespeicherten Uploads.',
  ],
  Export: [
    'Erstellt JSON-Exporte für Pakete, Audit-Logs oder Nutzer-/GDPR-Daten. Der Export verändert keine gespeicherten Datensätze.',
    'Nutzer-/GDPR-Exporte enthalten sensible personenbezogene Informationen und sollten entsprechend geschützt gespeichert und weitergegeben werden.',
  ],
  Validierung: [
    'Prüft einen vorhandenen Upload und speichert das Validierungsergebnis. Fehler werden als Validierungsstatus bzw. Report sichtbar.',
    'Die Validierung selbst veröffentlicht, verschiebt oder löscht keine Datei und ist kein allgemeines Freigabe-Gate für jede weitere Bot-Admin-Aktion.',
  ],
  Pakete: [
    'Hier verwaltest du Paketstatus, Quarantäne, Soft-Delete, Wiederherstellung und – nach zusätzlicher Bestätigung – endgültiges Löschen.',
    'Soft-Delete bleibt wiederherstellbar. Endgültiges Löschen ist nicht rückgängig zu machen und verwendet den Step-up-Bestätigungsfluss.',
  ],
  Nutzer: [
    'Hier suchst und filterst du Bot-Nutzer, kannst Konten sperren/entsperren sowie Herstellerfreigaben bearbeiten.',
    'Ein Passwort-Reset erzeugt ein neues zeitlich begrenztes Einmal-Passwort, widerruft noch offene alte OTPs und zeigt das neue Secret nur für die unmittelbare Weitergabe an.',
  ],
  Tickets: [
    'Zeigt Support-Tickets und ihren aktuellen Status im Bot-Admin-Bereich.',
    'Offene oder wartende Tickets können hier geschlossen werden; die Ansicht verspricht keine separate Antwortfunktion, wenn der jeweilige Ticketfluss sie nicht anbietet.',
  ],
  Selfroles: [
    'Hier verwaltest du Self-Role-Menüs eines ausgewählten Discord-Servers, fügst Optionen hinzu und kannst Menüs posten, aktivieren oder deaktivieren.',
    'Die Funktion ist Guild-gebunden und verwendet die vorhandene Selfrole-Datenhaltung.',
  ],
  'Loadout Creator': [
    'Bearbeite DayZ-Startausrüstung bzw. Fresh-Spawn-Loadouts visuell oder als JSON – ohne Discord-Anmeldung, Guild-, Slot- oder Nitrado-Kontext.',
    'Der Creator schreibt niemals automatisch auf einen Gameserver. Exportierte Daten müssen anschließend bewusst weiterverwendet oder hochgeladen werden.',
  ],
  'DayZ-Runtime-Katalog': [
    'Hinterlegt die privaten DayZ-Runtime-Exporte genau dieses Gameservers für den NPC Creator dieses Slots.',
    'Manifest/Fingerprint und Katalogzusammenhang werden geprüft. Der eigenständige Loadout Creator nutzt diese privaten Serverdaten nicht; es wird nichts auf den Gameserver geschrieben.',
  ],
  'Runtime-Katalog': [
    'Hinterlegt die privaten DayZ-Runtime-Exporte genau dieses Gameservers für den NPC Creator dieses Slots.',
    'Manifest/Fingerprint und Katalogzusammenhang werden geprüft. Der eigenständige Loadout Creator nutzt diese privaten Serverdaten nicht; es wird nichts auf den Gameserver geschrieben.',
  ],
  'NPC Creator': [
    'Bearbeitet getrennte Inserts für events.xml, cfgeventspawns.xml und cfgspawnabletypes.xml und prüft verknüpfte NPC-, Spawn- und Attachment-Daten.',
    'Vorschau, Validierung und Export bleiben vom Live-Server getrennt. Der NPC Creator schreibt keine Datei automatisch auf Nitrado.',
  ],
  'Playerspawn Creator': [
    'Erstellt und bearbeitet cfgplayerspawnpoints.xml verlustfrei mit exakten DayZ-X/Z-Koordinaten.',
    'Upload, Bearbeitung, Validierung, Diff und Download bleiben getrennt vom Serverbetrieb; es gibt keinen automatischen Live-Write zu Nitrado.',
  ],
  'Ignorelist-Generator': [
    'Erstellt eine cfgignorelist.xml aus dem verifizierten DayZ-Itemkatalog und erhält unbekannte bzw. modded Classnames kontrolliert.',
    'Der Generator arbeitet lokal im Toolfluss und schreibt keine Ignorelist automatisch auf Nitrado.',
  ],
  'Server Health': [
    'Zeigt ausschließlich persistierte Betriebs- und Fehlerdaten dieses Gameservers.',
    'Das Anzeigen oder Aktualisieren startet keine Nitrado-Operation und verändert keinen Serverzustand.',
  ],
  'Spieler-Zentrale': [
    'Führt Sessions, verifiziertes Linking, PvP, Economy, Whitelist, Bans und Fraktionen servergescoppt in einer read-only Ansicht zusammen.',
    'Eine bloße Namensähnlichkeit erzeugt keine Identitätszuordnung; der Live-Status benötigt aktuelle ADM-Evidenz.',
  ],
  Zeitmaschine: [
    'Vergleicht gespeicherte read-only Mirror-Snapshots dieses Gameservers.',
    'Unverändert wird nur bei identischen SHA-256-Werten behauptet; Restore, Deploy und Live-Write sind bewusst nicht Bestandteil dieser Ansicht.',
  ],
  'KI-Provider-Statistik': [
    'Zeigt Erfolgsquote, Latenz, Rate-Limits, Cooldowns und Anomalien der konfigurierten KI-Provider.',
    'Die Ansicht dient der Diagnose und ändert durch das Anzeigen keine Provider-Konfiguration.',
  ],
  'Admin-Stats & Monitor': [
    'Zeigt aktuelle Systemwerte des Bot-Admin-Bereichs zur technischen Diagnose.',
    'Die Ansicht liest vorhandene Statusdaten und ändert keine Einstellungen.',
  ],
  'Error Report': [
    'Zeigt erfasste technische Fehler zur Ursachenanalyse.',
    'Das Lesen eines Fehlers ändert keinen Bot-Zustand und führt keine automatische Reparatur aus.',
  ],
};

function normalizeTitle(title: string): string {
  return title.replace(/\s+/g, ' ').trim();
}

export function functionHelpFor(title: string): FunctionHelp {
  const normalized = normalizeTitle(title);
  const text = HELP[normalized];
  return {
    title: normalized || 'Bereichshilfe',
    text: text ?? [],
    available: Boolean(text),
  };
}
